import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  dataCurta,
  evolucaoDaTaxa,
  plural,
  taxaDeAcerto,
  type DiaDaSerie,
  type LinhaDeCategoria,
  type PainelDeEstatisticas,
  type PontoDaEvolucao,
} from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Metrica } from "../componentes/Metrica.js";
import { SeletorSegmentado } from "../componentes/Controles.js";
import { usePraticar } from "../estado/praticar.js";
import { useSessao } from "../estado/sessao.js";
import { useTrilhaAtiva } from "../estado/trilhas.js";

/**
 * Estatísticas — tela 6 do SPEC.
 *
 * Três perguntas, nesta ordem: quanto eu fiz, se está melhorando, e onde dói.
 * Volume e taxa vêm do servidor já contados; a evolução é derivada da mesma
 * série pela função pura de `@noesis/shared`, para o gráfico e o total nunca
 * discordarem.
 *
 * Todo gráfico aqui é desenho **e** tabela: o SVG é `aria-hidden` e ao lado
 * dele vai a mesma série em `<table>` para leitor de tela, como o heatmap já
 * faz. Gráfico sem tabela é dado que existe só para quem enxerga.
 */

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; painel: PainelDeEstatisticas }
  | { nome: "erro"; mensagem: string };

export function Estatisticas() {
  const trilha = useTrilhaAtiva();
  const { limites } = useSessao();
  const [janela, setJanela] = useState(limites.estatistica.padrao);
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });

  const carregar = useCallback(async (janelaDias: number) => {
    setEstado({ nome: "carregando" });
    try {
      setEstado({ nome: "pronto", painel: await pedir("estatistica.painel", { corpo: { janelaDias } }) });
    } catch (e) {
      setEstado({ nome: "erro", mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar." });
    }
  }, []);

  useEffect(() => {
    if (trilha) void carregar(janela);
  }, [trilha, janela, carregar]);

  if (!trilha) {
    return (
      <Moldura>
        <section className="estado">
          <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
          <p className="estado__texto">As estatísticas são de uma trilha por vez.</p>
          <Link className="botao" to="/trilhas">
            Escolher uma trilha
          </Link>
        </section>
      </Moldura>
    );
  }

  return (
    <Moldura>
      {/* O período num seletor único, com o intervalo ao lado (ADR 039). */}
      <div className="numeros__periodo">
        <SeletorSegmentado
          rotulo="Janela de tempo"
          opcoes={limites.estatistica.janelas.map((d) => ({ valor: String(d), rotulo: rotuloDaJanela(d) }))}
          valor={String(janela)}
          aoMudar={(v) => setJanela(Number(v))}
        />
        {estado.nome === "pronto" && (
          <p className="numeros__intervalo t-anotacao t-2">
            {dataCurta(estado.painel.de)} a {dataCurta(estado.painel.hoje)}
          </p>
        )}
      </div>

      {estado.nome === "carregando" && (
        <div aria-busy="true">
          <span className="so-leitor" role="status">
            Carregando as estatísticas
          </span>
          <div className="esqueleto esqueleto--faixa" />
          <div className="esqueleto esqueleto--bloco" />
        </div>
      )}

      {estado.nome === "erro" && (
        <section className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar(janela)}>
            Tentar de novo
          </button>
        </section>
      )}

      {estado.nome === "pronto" && <Painel painel={estado.painel} />}
    </Moldura>
  );
}

function Painel({ painel }: { painel: PainelDeEstatisticas }) {
  if (painel.respondidos === 0) {
    return (
      <section className="estado">
        <h2 className="estado__titulo">Nada respondido nesta janela</h2>
        <p className="estado__texto">
          Nos últimos {painel.janelaDias} dias não há resposta registrada nesta trilha. Uma sessão de estudo
          preenche a primeira barra — ou escolha uma janela maior, se você parou por um tempo.
        </p>
        <Link className="botao" to="/estudar">
          Estudar agora
        </Link>
      </section>
    );
  }

  const taxa = taxaDeAcerto(painel.acertos, painel.respondidos);
  const evolucao = evolucaoDaTaxa(painel.serie);

  return (
    <>
      <section className="numeros__resumo" aria-labelledby="resumo-titulo">
        <h2 className="so-leitor" id="resumo-titulo">
          De {dataCurta(painel.de)} a {dataCurta(painel.hoje)}
        </h2>
        <div className="faixa-metricas faixa-metricas--quatro">
          <Metrica rotulo="Respondidos" valor={String(painel.respondidos)} nota={`${painel.janelaDias} dias`} />
          <Metrica
            rotulo="Taxa de acerto"
            valor={emPorcento(taxa)}
            nota={`${painel.acertos} de ${painel.respondidos}`}
          />
          <Metrica rotulo="Tempo médio" valor={emSegundos(painel.tempoMedioMs)} nota="por item" />
          <Metrica
            rotulo="Ofensiva"
            valor={`${painel.ofensiva.atual}`}
            nota={`recorde ${painel.ofensiva.recorde}`}
          />
        </div>
        {painel.itensArquivadosIgnorados > 0 && (
          <p className="t-ajuda">
            {painel.itensArquivadosIgnorados} resposta(s) da janela são de itens arquivados: contam no volume
            do dia, mas ficam fora do tempo médio e do acerto por categoria.
          </p>
        )}
        {/*
          RN-65: a média emagrecida precisa dizer o que ficou de fora. O tempo é
          cronômetro de parede, e a questão que ficou com a aba aberta a noite
          inteira não é uma medição de quanto se levou para responder.
        */}
        {painel.temposIgnorados > 0 && (
          <p className="t-ajuda">
            {painel.temposIgnorados} resposta(s) passaram do teto de tempo e ficaram fora do tempo médio: continuam
            contando no volume do dia e no acerto.
          </p>
        )}
      </section>

      {/*
        Os dois gráficos lado a lado na largura ampla, empilhados no estreito
        (RN-51). O dado é o mesmo nos dois casos — muda o arranjo, não o
        conteúdo (RN-30). Lado a lado eles se comparam: o pico de volume de um
        dia e a queda da taxa no mesmo dia são a mesma história, e empilhados a
        um scroll de distância eles deixam de ser lidos juntos.
      */}
      <div className="graficos-lado-a-lado">
        <div className="graficos-lado-a-lado__grade">
        <section className="grafico__cartao" aria-labelledby="volume-titulo">
          <div className="grafico__topo">
            <h2 className="t-secao" id="volume-titulo">
              Itens por dia
            </h2>
            <p className="t-anotacao-p">pico de {Math.max(0, ...painel.serie.map((d) => d.respondidos))} num dia</p>
          </div>
          <GraficoDeBarras serie={painel.serie} />
          <p className="grafico__legenda t-anotacao-p">
            <span className="grafico__chave grafico__chave--acerto" /> acertos
            <span className="grafico__chave" /> erros
          </p>
          <TabelaDaSerie serie={painel.serie} legenda="Itens respondidos por dia, na janela escolhida" />
        </section>

        <section className="grafico__cartao" aria-labelledby="evolucao-titulo">
          <div className="grafico__topo">
            <h2 className="t-secao" id="evolucao-titulo">
              Taxa de acerto
            </h2>
            <p className="t-anotacao-p">acumulada {emPorcento(taxa)}</p>
          </div>
          <GraficoDeLinha pontos={evolucao} />
          <p className="grafico__legenda t-anotacao-p">fina: o dia · grossa: acumulada na janela</p>
          <TabelaDaEvolucao pontos={evolucao} />
        </section>
        </div>
      </div>

      <section className="numeros__categorias" aria-labelledby="categorias-titulo">
        <div className="grafico__topo">
          <h2 className="t-secao" id="categorias-titulo">
            Acerto por categoria
          </h2>
          <p className="t-anotacao-p">mais fraca primeiro</p>
        </div>
        {painel.porCategoria.length === 0 ? (
          <p className="estado__texto">
            Nenhuma resposta com categoria viva nesta janela. Itens arquivados não entram aqui.
          </p>
        ) : (
          <TabelaDeCategorias linhas={painel.porCategoria} />
        )}
      </section>
    </>
  );
}

/**
 * Barras de volume. A geometria é `viewBox` com coordenadas relativas, então o
 * mesmo desenho serve a qualquer largura sem media query — o SVG estica e a
 * proporção das barras não muda.
 */
function GraficoDeBarras({ serie }: { serie: DiaDaSerie[] }) {
  const pico = Math.max(1, ...serie.map((d) => d.respondidos));
  const segundo = Math.max(0, ...serie.map((d) => d.respondidos).filter((v, i, a) => i !== a.indexOf(pico)));
  // Um dia fora da curva achata todos os outros. Quando o pico passa do dobro
  // do segundo maior, a escala para no segundo (com folga) e o pico é cortado
  // e rotulado na legenda (ADR 039).
  const cortado = segundo > 0 && pico > 2 * segundo;
  const teto = cortado ? Math.ceil(segundo * 1.25) : pico;
  const diaDoPico = serie.find((d) => d.respondidos === pico)?.data;
  const larguraDaColuna = 100 / serie.length;

  return (
    <div className="grafico cartao-mao">
      <svg className="grafico__svg" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
        <line className="grafico__eixo" x1="0" y1="40" x2="100" y2="40" />
        {serie.map((d, i) => {
          const altura = (Math.min(d.respondidos, teto) / teto) * 40;
          const acertos = d.respondidos === 0 ? 0 : (Math.min(d.acertos, teto) / teto) * 40;
          return (
            <g key={d.data}>
              <rect
                className="grafico__barra"
                x={i * larguraDaColuna}
                y={40 - altura}
                width={larguraDaColuna * 0.8}
                height={altura}
              />
              <rect
                className="grafico__barra grafico__barra--acerto"
                x={i * larguraDaColuna}
                y={40 - acertos}
                width={larguraDaColuna * 0.8}
                height={acertos}
              />
            </g>
          );
        })}
      </svg>
      {cortado && diaDoPico && (
        <p className="grafico__rodape t-ajuda">
          O pico de {pico} em {dataCurta(diaDoPico)} passa do topo, que está em {teto}.
        </p>
      )}
    </div>
  );
}

function GraficoDeLinha({ pontos }: { pontos: PontoDaEvolucao[] }) {
  const largura = 100 / Math.max(1, pontos.length - 1);
  const y = (taxa: number) => 40 - taxa * 40;

  const caminho = (pegar: (p: PontoDaEvolucao) => number | null): string => {
    let d = "";
    let abriu = false;
    pontos.forEach((p, i) => {
      const v = pegar(p);
      // Dia sem resposta não vira zero: a linha corta e recomeça depois. Ligar
      // os dois lados desenharia um mergulho que não aconteceu.
      if (v === null) {
        abriu = false;
        return;
      }
      d += `${abriu ? "L" : "M"}${i * largura} ${y(v)} `;
      abriu = true;
    });
    return d.trim();
  };

  return (
    <div className="grafico grafico--linha cartao-mao cartao-mao--espelho">
      <div className="grafico__escala t-anotacao-p" aria-hidden="true">
        <span>100</span>
        <span>50</span>
        <span>0</span>
      </div>
      <svg className="grafico__svg" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
        <line className="grafico__eixo" x1="0" y1="40" x2="100" y2="40" />
        <line className="grafico__eixo" x1="0" y1="0" x2="0" y2="40" />
        <line className="grafico__meio" x1="0" y1="20" x2="100" y2="20" />
        <path className="grafico__dia" d={caminho((p) => p.taxaDoDia)} />
        <path className="grafico__linha" d={caminho((p) => p.taxaAcumulada)} />
      </svg>
    </div>
  );
}

function TabelaDaSerie({ serie, legenda }: { serie: DiaDaSerie[]; legenda: string }) {
  return (
    <div className="so-leitor">
    <table>
      <caption>{legenda}</caption>
      <thead>
        <tr>
          <th scope="col">Dia</th>
          <th scope="col">Respondidos</th>
          <th scope="col">Acertos</th>
        </tr>
      </thead>
      <tbody>
        {serie.map((d) => (
          <tr key={d.data}>
            <th scope="row">{dataCurta(d.data)}</th>
            <td>{d.respondidos}</td>
            <td>{d.acertos}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function TabelaDaEvolucao({ pontos }: { pontos: PontoDaEvolucao[] }) {
  return (
    <div className="so-leitor">
    <table>
      <caption>Taxa de acerto do dia e acumulada, na janela escolhida</caption>
      <thead>
        <tr>
          <th scope="col">Dia</th>
          <th scope="col">Taxa do dia</th>
          <th scope="col">Taxa acumulada</th>
        </tr>
      </thead>
      <tbody>
        {pontos.map((p) => (
          <tr key={p.data}>
            <th scope="row">{dataCurta(p.data)}</th>
            <td>{emPorcento(p.taxaDoDia)}</td>
            <td>{emPorcento(p.taxaAcumulada)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

/**
 * Acerto por categoria em tabela, a mais fraca primeiro (ADR 039). A cor da
 * barra é a faixa — abaixo de 50% erro, abaixo de 65% âmbar — e as duas piores
 * ganham "Praticar", que monta uma sessão só daquele assunto.
 */
function TabelaDeCategorias({ linhas }: { linhas: readonly LinhaDeCategoria[] }) {
  const { praticar, ocupado } = usePraticar();
  const ordenadas = [...linhas].sort(
    (a, b) => (taxaDeAcerto(a.acertos, a.respondidos) ?? 1) - (taxaDeAcerto(b.acertos, b.respondidos) ?? 1),
  );
  return (
    <ul className="tabela-categorias">
      <li className="tabela-categorias__linha tabela-categorias__cabecalho t-anotacao-p" aria-hidden="true">
        <span>categoria</span>
        <span />
        <span>acerto</span>
        <span>certas</span>
        <span />
      </li>
      {ordenadas.map((l, i) => {
        const taxa = taxaDeAcerto(l.acertos, l.respondidos);
        const faixa = taxa === null ? "" : taxa < 0.5 ? "fraca" : taxa < 0.65 ? "media" : "boa";
        return (
          <li key={l.categoriaId} className={`tabela-categorias__linha tabela-categorias__linha--${faixa}`}>
            <span className="tabela-categorias__nome">{l.caminho.join(", ")}</span>
            <span
              className="categoria__barra tabela-categorias__barra"
              style={{ ["--fracao" as string]: String(taxa ?? 0) }}
              role="img"
              aria-label={`${l.acertos} acertos em ${l.respondidos} respostas`}
            >
              <span className="categoria__barra-cheia" />
            </span>
            <span className="tabela-categorias__taxa dado">{emPorcento(taxa)}</span>
            <span className="tabela-categorias__conta dado">
              {l.acertos} de {l.respondidos}
            </span>
            <span className="tabela-categorias__acao">
              {i < 2 && (taxa ?? 1) < 1 && (
                <button
                  type="button"
                  className="link-mao"
                  disabled={ocupado !== null}
                  onClick={() => void praticar(l.categoriaId, { categoriaId: l.categoriaId })}
                >
                  {ocupado === l.categoriaId ? "montando…" : "praticar"}
                </button>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">
        {children}
      </div>
    </div>
  );
}

/** "—" e não "0%": sem denominador não existe taxa, e zero seria uma mentira. */
function emPorcento(taxa: number | null): string {
  if (taxa === null) return "—";
  return `${Math.round(taxa * 100)}%`;
}

function emSegundos(ms: number | null): string {
  if (ms === null) return "—";
  return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
}

function rotuloDaJanela(dias: number): string {
  if (dias % 365 === 0) return plural(dias / 365, "ano");
  if (dias % 30 === 0) return plural(dias / 30, "mês", "meses");
  // Sete dias se lê "7 dias", não "1 semana": a faixa fala em dias, e trocar a
  // unidade só no primeiro degrau faria a régua parecer outra.
  if (dias % 7 === 0 && dias !== 7) return plural(dias / 7, "semana");
  return plural(dias, "dia");
}
